import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchMyGroups, fetchGroupDetail, createGroup, updateGroup, deleteGroup, bulkSetGroupMembers } from '../api/groups'

/**
 * All groups visible to the current user (owned + member of).
 */
export function useMyGroups() {
  return useQuery({
    queryKey: ['my-groups'],
    queryFn: fetchMyGroups,
    staleTime: 60_000,
  })
}

/**
 * Single group with full member data.
 */
export function useGroupDetail(groupId) {
  return useQuery({
    queryKey: ['group', groupId],
    queryFn: () => fetchGroupDetail(groupId),
    enabled: !!groupId,
    staleTime: 60_000,
  })
}

/**
 * Create a new group.
 */
export function useCreateGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
    },
  })
}

/**
 * Update a group's name.
 */
export function useUpdateGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ groupId, name }) => updateGroup(groupId, { name }),
    onSuccess: (_data, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['group', groupId] })
    },
  })
}

/**
 * Delete a group.
 */
export function useDeleteGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
    },
  })
}

/**
 * Save the full member list for a group (replace all).
 */
export function useSaveGroupMembers() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ groupId, playerIds }) => bulkSetGroupMembers(groupId, playerIds),
    onSuccess: (_data, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['group', groupId] })
    },
  })
}
